// CTW-DURMAH-STUDENT-PAINPOINTS-SEO-RESOURCE-EXPANSION-02:
// Student journey cluster article: the Year 1 to Year 2 transition. The
// most-asked, least-answered question ("is second year harder?") gets an
// honest craft answer today only from forums. Claim-safety: classification
// weighting and module sequences vary by university and are always framed
// as "check your programme handbook"; no percentages, no invented policies.

import type { ResourceSection, ResourceSourceGroup } from "./types";

export const secondYearLawWhatChangesSections: ResourceSection[] = [
  {
    id: "the-question-everyone-asks",
    heading: "The question everyone asks, answered honestly",
    paragraphs: [
      "This guide uses a three-year LLB as its example. Degree structures, classification weightings and recruitment timetables vary by programme. The professional-careers sources here mainly concern England and Wales; Scotland and Northern Ireland have different qualification routes. Check your own handbook and careers service rather than treating this as a universal timetable.",
      "Every summer, thousands of law students type some version of the same question: is second year really that much harder? The forums say yes, loudly and unhelpfully. The honest answer is that second year is less a difficulty spike than a rules change. Three things shift at once: your marks usually start to count, the marking expectations move up a level, and your calendar suddenly has to hold applications and commitments alongside the degree.",
      "None of the three is unmanageable, and students who understand the shift in advance handle it far better than students who discover it in week five. Universities themselves acknowledge the step-up: library and study-skills teams describe second year as the point where assessments start to count, the hand-holding reduces, and independent thinking is expected. That is this page's whole purpose: to replace the vague dread with a specific picture and a plan.",
    ],
    links: [
      {
        label: "Leeds Beckett University Library: step up to second year",
        href: "https://www.leedsbeckett.ac.uk/blogs/library/2021/09/step-up-to-second-year/",
      },
    ],
  },
  {
    id: "marks-start-counting",
    heading: "Your marks usually start counting",
    paragraphs: [
      "At many UK universities, first year must be passed but contributes little or nothing to your final degree classification, while second-year and final-year marks carry real weight, with later years usually weighted more heavily. The exact split varies between universities and programmes, so the authoritative answer for you lives in one place: your own programme handbook or your university's published classification rules. Read them once, early, so you know precisely what you are playing for.",
      "The psychological reframe matters more than the arithmetic. First year was the same game with a practice scoreboard: real teaching, real feedback, no lasting damage. Nothing about the work is suddenly alien; the stakes simply arrived. Treat that as useful pressure rather than threat, and remember that recruiters also see module-level results from every year, so the habit of caring was never optional anyway.",
    ],
    links: [
      {
        label: "University of Bristol: how degree classification weighting works (one university's example)",
        href: "https://www.bristol.ac.uk/academic-quality/degree-outcomes-statement/degree-classification-guide/",
      },
    ],
    callout: {
      title: "One document to find this week",
      body: "Your programme handbook's section on assessment and degree classification. It answers, for your university specifically, everything this page can only describe in general terms.",
    },
  },
  {
    id: "the-expectation-shift",
    heading: "The real jump: from describing law to arguing about it",
    paragraphs: [
      "Ask markers what separates second-year work from first-year work and the same phrase appears everywhere: critical analysis. In first year, accurately explaining the law earns solid marks, because learning to explain it accurately is genuinely hard. In second year the accurate explanation becomes the entry fee, and the marks migrate to what you do with it: evaluating the reasoning, weighing competing authorities, engaging with academic criticism, and committing to a defensible position.",
      "Concretely, critical analysis in law looks like this: showing why a decision's reasoning is strong or strained, not just what it held. Noticing where two authorities pull in different directions and arguing which should prevail. Using journal articles and academic commentary as sparring partners rather than decoration. Ending paragraphs with your assessment rather than a summary. University writing guidance says the same thing in its own words: keep description minimal, and show your interpretation of the material and the position you take on it. If your first-year feedback ever said words like descriptive, more analysis needed or engage with the arguments, that comment was aimed exactly at this shift, and it is entirely learnable.",
    ],
    links: [
      {
        label: "University of Leeds Library: critical writing, moving beyond description",
        href: "https://library.leeds.ac.uk/info/1401/academic-skills/105/critical-thinking/5",
      },
    ],
  },
  {
    id: "the-modules-get-denser",
    heading: "The modules get denser",
    paragraphs: [
      "Module sequences differ between universities, but second year commonly brings some of the subjects students find the degree's steepest: areas built on long chains of interlocking authority, where doctrine layers on doctrine and week eight makes no sense without week three. The volume of reading may not rise dramatically; the interconnectedness does.",
      "The counter is consistency rather than heroics. Falling three weeks behind in a cumulative subject costs far more than it did in a survey module, because each seminar assumes the last. The reading triage, case-briefing and layered-reading methods from our study skills guides stop being nice habits and start being load-bearing. Second year is the year the students with a method visibly pull ahead of the students with stamina.",
    ],
  },
  {
    id: "the-calendar-collision",
    heading: "The calendar collision nobody warns you about",
    paragraphs: [
      "For most students on a three-year LLB, second year is the penultimate year, which means vacation scheme and placement applications open during it, many in the autumn term. Add mooting, society commitments, perhaps part-time work, and the real second-year difficulty comes into focus: it is not that any single demand grows monstrous, it is that they all arrive in the same weeks.",
      "The students who cope are the ones who treat the collision as a planning problem rather than a character test. Map the term before it starts: application deadlines from the careers databases, assessment dates from your module guides, fixed commitments, and honest weekly hours for each. Applications done in small early sessions beat applications done in deadline-week panic, and our internship readiness guide covers that rhythm in detail. Deadlines belong to the firms' and chambers' own pages, so always confirm dates there rather than trusting summaries.",
    ],
    links: [
      {
        label: "LawCareers.Net: when to apply for a vacation scheme",
        href: "https://www.lawcareers.net/Explore/Oracle/01092025-When-should-I-apply-for-a-vacation-scheme",
      },
    ],
  },
  {
    id: "the-skills-audit",
    heading: "The August skills audit",
    paragraphs: [
      "The single highest-value thing you can do between first and second year takes one honest afternoon. Collect every piece of marked feedback from first year and read it in one sitting, looking for repeated comments rather than individual wounds. Three essays that all say structure drifts or authority missing are not three problems; they are one problem, named three times, and one problem is fixable before October.",
      "Then check the foundation skills against our study skills cluster: can you brief a case in half a page without re-reading the judgment twice? Does your problem-question answer loop issue by issue with authority at every step? Is your OSCOLA clean enough that referencing costs you nothing? Whatever wobbles, the fix costs a few focused hours now and pays across every module of the two years that count.",
    ],
  },
  {
    id: "wellbeing-and-pacing",
    heading: "Pacing, and the illusion that everyone else is fine",
    paragraphs: [
      "Second year is also where workload stress gets real for many students, and where the illusion that everyone else is coping effortlessly does its quiet damage. They are not; law cohorts are simply good at performing composure. Feeling stretched in a year where marks count, applications loom and the modules deepen is a proportionate response to a genuinely full year, not evidence you are in the wrong degree.",
      "The practical protections are unglamorous: a sustainable weekly rhythm with actual rest built in, one or two commitments chosen deliberately rather than five accepted by drift, and asking for help early, from module tutors on the academic side and from your university's support services when the pressure is about more than the reading. Our wellbeing guide collects the wider support routes, including the charity dedicated to the legal community.",
    ],
  },
  {
    id: "a-first-month-plan",
    heading: "A concrete first-month plan",
    paragraphs: ["If you want second year to start on the front foot, this sequence covers the essentials:"],
    bullets: [
      "Before term: read your programme handbook's assessment and classification rules, once, properly.",
      "Before term: do the feedback audit above and fix the one repeated weakness it reveals.",
      "Week one: map the term's assessment dates and application deadlines into one calendar you actually look at.",
      "Week one: set your reading rhythm, which days, which modules, what triage rule when the list is too long.",
      "Weeks two to four: draft the reusable core of your applications early, outside deadline pressure.",
      "Ongoing: one deliberate extracurricular done well, a moot, pro bono, a society role, beats four done thinly.",
      "Ongoing: end each week by noting what fell behind, and decide its catch-up slot before Monday.",
    ],
  },
  {
    id: "where-durmah-fits",
    heading: "Where Durmah fits",
    paragraphs: [
      "Second year rewards exactly what Durmah is built around: steady understanding over cramming, structured reasoning over description, and honest awareness of what you know versus what you have merely read. Use it to test yourself on the interlocking doctrines as they stack up, to talk through an argument before you commit it to an essay, and to keep your lectures and modules organised in one place while the calendar fills.",
      "What it will not do is absorb the year for you. The critical analysis your markers now want is a muscle, and it grows only in your own reasoning. Durmah's role is the practice partner: patient, grounded in sources, and entirely uninterested in doing your thinking for you, because the second-year version of you that firms interview and finals examine has to be real.",
    ],
  },
];

export const secondYearLawWhatChangesSources: ResourceSourceGroup[] = [
  {
    topic: "The step-up, in universities' own words",
    sources: [
      {
        label: "Leeds Beckett University Library: step up to second year",
        href: "https://www.leedsbeckett.ac.uk/blogs/library/2021/09/step-up-to-second-year/",
      },
      {
        label: "University of Leeds Library: critical writing",
        href: "https://library.leeds.ac.uk/info/1401/academic-skills/105/critical-thinking/5",
      },
      {
        label: "University of Bristol: degree classification guide (an example of year weighting)",
        href: "https://www.bristol.ac.uk/academic-quality/degree-outcomes-statement/degree-classification-guide/",
      },
    ],
  },
  {
    topic: "Careers timing",
    sources: [
      {
        label: "LawCareers.Net: when to apply for a vacation scheme",
        href: "https://www.lawcareers.net/Explore/Oracle/01092025-When-should-I-apply-for-a-vacation-scheme",
      },
      {
        label: "LawCareers.Net: first-year law students, what you need to know",
        href: "https://www.lawcareers.net/Solicitors/First-Year-Law-Students/What-You-Need-To-Know",
      },
    ],
  },
  {
    topic: "Study skills at the next level",
    sources: [
      {
        label: "University of Bristol Law School: study skills series",
        href: "https://legalresearch.blogs.bris.ac.uk/2021/09/study-skills-series-an-introduction/",
      },
    ],
  },
];
